/*
 * File: LegalNavigation.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 13, 2022 at 5:15 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 6:35 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useRef, useState, useEffect, useMemo
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

// Hooks
import useMediaQuery from '../../hooks/useMediaQuery';

// Components
import { Typography } from '../Typography';
import { Padding } from '../Padding';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';
import { mobileBreakpoint } from '../../styles/constants';

// Styles
import * as S from './LegalNavigation.styles';


/**
 * Component
 */

export const LegalNavigation = ({
	children, allowOverflow, navigationSections, mobileNavigationHeight
}) => {

	// Set state
	const [sidebarBreakpoint, setSidebarBreakpoint] = useState(-1);
	const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);

	// Get location
	const location = useLocation();

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Check if mobile screen size
	const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}em)`);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Create references for elements
	const sidebarRef = useRef();
	const contentRef = useRef();
	const isMounted = useRef(true);

	// Check if link is action function
	const isLinkActive = (to) => location.pathname === to || location.pathname === `${locale.localePath}${to}`;

	// Get flattened links
	const flattendedLinks = useMemo(() => [].concat(...navigationSections.map(({ links }) => links)), [navigationSections]);

	// Handle mobile navigation visibility
	const toggleMobileNavigation = () => {
		if (mobileNavigationOpen == null) setMobileNavigationOpen(false);
		else setMobileNavigationOpen(!mobileNavigationOpen);
	};

	// Handle actions on mobile mode change
	useEffect(() => {

		// Hide mobile navigation visibility
		setMobileNavigationOpen(false);

	}, [isMobile]);

	// Handle actions on component load
	useEffect(() => {

		// Initialize temporary state
		isMounted.current = true;

		// Attach scroll listener
		if (isMounted.current) {
			document.addEventListener('scroll', () => {
				if (isMounted.current && sidebarRef.current != null && contentRef.current != null) {

					// Get parameters
					const { bottom: sidebarBottom } = sidebarRef.current.getBoundingClientRect();
					const { bottom: contentBottom } = contentRef.current.getBoundingClientRect();

					// Update sidebar position class
					if (contentBottom <= sidebarBottom && sidebarBreakpoint === -1) {
						if (!sidebarRef.current.classList.contains('bottom')) {
							sidebarRef.current.classList.add('bottom');
							setSidebarBreakpoint(sidebarBottom);
						}
					} else if (contentBottom > sidebarBreakpoint && sidebarBreakpoint !== -1 && sidebarRef.current.classList.contains('bottom')) {
						sidebarRef.current.classList.remove('bottom');
						setSidebarBreakpoint(-1);
					}
				}
			}, true);
		}

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, [sidebarBreakpoint]);

	// Render navigation components function
	const renderNavigationItems = () => (navigationSections.map((section) => (
		<S.NavigationSection key={section.title}>
			<Typography tag="h5" weight="semibold">{section.title}</Typography>
			{section.links.map((link) => (
				<S.Link to={link.url} key={link.url}>
					{isLinkActive(link.url) && <S.LinkActive />}
					{link.label}
				</S.Link>
			))}

		</S.NavigationSection>
	))
	);

	const renderPageTitle = () => {
		let title = '';
		flattendedLinks.forEach(({ url, label }) => {
			if (isLinkActive(url)) {
				title = label;
			}
		});
		return title;
	};

	// Render component
	return (
		<S.LegalNavigation>
			<S.InnerNavigation>

				{/* Mobile Navigation */}
				<S.MobileNavigation className="isMobile">

					{/* Mobile Header */}
					<S.MobileHeader onClick={toggleMobileNavigation}>
						<Padding left right dynamic>

							{/* Title */}
							<Typography tag="h5" weight="medium">{renderPageTitle()}</Typography>

							{/* Toggle */}
							<S.MobileNavigationToggle className="animate" $isOpen={mobileNavigationOpen} icon={['far', 'angle-down']} />
						</Padding>
					</S.MobileHeader>

					{/* Mobile Dropdown */}
					<S.MobileDropdown
						left
						right
						dynamic
						className="animate"
						$isOpen={mobileNavigationOpen}
						$elementHeight={mobileNavigationHeight}
					>
						<Padding left right dynamic>
							{renderNavigationItems()}
						</Padding>
					</S.MobileDropdown>
				</S.MobileNavigation>

				{/* Content */}
				<S.Wrapper left right dynamic>

					{/* Sidebar Navigation */}
					<S.SidebarNavigation className="isNotMobile" ref={sidebarRef}>

						{/* Title */}
						<Typography tag="h2" weight="bold">
							<Trans t={t} i18nKey="legalNavigation.title">
								Legal
							</Trans>
						</Typography>

						{/* Navigation Items */}
						{renderNavigationItems()}

					</S.SidebarNavigation>

					{/* Content */}
					<S.Content className="paragraphContent" id="scroller" $allowOverflow={allowOverflow}>
						<S.ContentPadding ref={contentRef}>
							{children}
						</S.ContentPadding>
					</S.Content>
				</S.Wrapper>
			</S.InnerNavigation>
		</S.LegalNavigation>
	);
};


/**
 * Configuration
 */

LegalNavigation.displayName = 'LegalNavigation';
LegalNavigation.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	allowOverflow: PropTypes.bool,
	mobileNavigationHeight: PropTypes.number,
	navigationSections: PropTypes.arrayOf(PropTypes.shape())
};
LegalNavigation.defaultProps = {
	children: null,
	allowOverflow: false,
	mobileNavigationHeight: 500,
	navigationSections: []
};
